import { useEffect, useId, useState } from "react";
import {
  chessArrow,
  type ChessArrow as Arrow,
  type ChessSquareStyles as CustomSquareStyles,
  type ChessSquare as Square,
} from "@/shared/components/chessboard";
import { Box, IconButton, Typography } from "@mui/material";
import SkipPreviousIcon from "@mui/icons-material/SkipPrevious";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import SkipNextIcon from "@mui/icons-material/SkipNext";
import type { MessageBoardState } from "../messaging/chat.store";
import type { BoardSegment } from "./parse-board-markers";
import { fetchGameAnalysis, type AnalyzedMoveResult } from "@/shared/api/engine.api";
import { BestMoveNavigator } from "./BestMoveNavigator";
import { CapturedPieces } from "@/features/board/review/CapturedPieces";
import { evaluatedLineStore } from "../evaluation/live-eval";
import { BoardFrame } from "@/shared/components/chessboard/BoardFrame";
import { hasUsableEvaluation, normalizeEvaluation } from "@/shared/components/evaluation";
import type { EvaluationScore } from "@chessla/shared";
import { useProgressiveEvaluation } from "@/features/board/review/use-progressive-evaluation";
import { inlineMoveEvidencePresentation } from "./inline-move-evidence";
import { ChesslaBoardSurface } from "@/features/board/surface";
import { findReferencedMoveIndex, movesInRange, recordedPly } from "./inline-board-navigation";
import { InlineMoveRail } from "./InlineMoveRail";
import SettingsIcon from "@mui/icons-material/Settings";
import { useBoardPreferences, ReviewBoardSettingsDialog, REVIEW_EVALUATION_DEPTH } from "@/features/board";

const DEFAULT_HIGHLIGHT_COLOR = "rgba(255, 170, 0, 0.5)";
const DEFAULT_ARROW_COLOR = "#3b82f6";

/**
 * A chessboard rendered inline within a chat message bubble, with an
 * optional move navigator below it when the position is from an
 * analyzed game. The navigator controls THIS board's position directly
 * (no second board spawned) — stepping through moves updates the same
 * board in place.
 */
export const InlineBoard = ({
  board,
  title,
  username,
}: {
  board: MessageBoardState | BoardSegment;
  title?: string | null;
  username?: string | null;
}) => {
  const { settings, change } = useBoardPreferences("nikola");
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [markerFocus, setMarkerFocus] = useState<{ fen: string; square: Square } | null>(null);
  // A unique-per-mounted-instance suffix — boardId must never collide
  // between two InlineBoard instances even when they show the exact
  // same game/FEN (e.g. the same critical moment referenced twice in
  // one chat), otherwise their scoped SVG marker IDs collide too and
  // arrow colors bleed between them exactly as if unscoped. Content
  // alone (gameId/fen) is not a valid uniqueness key for this — only
  // the component instance is.
  const instanceId = useId();
  const [moves, setMoves] = useState<AnalyzedMoveResult[]>([]);
  const [navIndex, setNavIndex] = useState(-1); // -1 = showing original message position
  const [navLoaded, setNavLoaded] = useState(false);
  const [totalPlies, setTotalPlies] = useState<number | null>(null);
  const [derivedOrientation, setDerivedOrientation] = useState<"white" | "black">(board.orientation);
  const [opponent, setOpponent] = useState<string | null>(null);
  /** The resolved FEN from DB — used for compare boards and as fallback when positionRef is set */
  const [resolvedFen, setResolvedFen] = useState<string | null>(null);

  useEffect(() => {
    if (!board.gameId) return;
    fetchGameAnalysis(board.gameId)
      .then((analysis) => {
        let gameMoves = analysis.moves;
        const finalMove = analysis.moves.at(-1);
        setTotalPlies(finalMove ? recordedPly(finalMove) : null);

        // Store opponent name from the API (way more reliable than regex)
        if (analysis.opponent) setOpponent(analysis.opponent);

        // Determine the target move number — from positionRef (reliable) or FEN fallback
        const positionRef = "positionRef" in board ? board.positionRef : null;
        const targetReference = positionRef
          ? { moveNumber: positionRef.moveNumber, ply: positionRef.ply ?? null }
          : null;

        // If plyRange is set, scope the navigator to only those plies
        const plyRange = "plyRange" in board ? board.plyRange : null;
        if (plyRange) {
          gameMoves = movesInRange(gameMoves, plyRange);
        } else {
          // Auto-scope: find the target position and show ±3 moves around it
          let scopeIdx = -1;
          if (targetReference) {
            scopeIdx = findReferencedMoveIndex(gameMoves, targetReference);
          } else {
            scopeIdx = gameMoves.findIndex((m) => m.fenBefore === board.fen);
            if (scopeIdx < 0) {
              const fenParts = board.fen.split(" ");
              const fullmoveNumber = Number.parseInt(fenParts[fenParts.length - 1], 10);
              if (!Number.isNaN(fullmoveNumber)) {
                scopeIdx = findReferencedMoveIndex(gameMoves, { moveNumber: fullmoveNumber, ply: null });
              }
            }
          }
          if (scopeIdx >= 0 && board.arrows.length > 0) {
            const scopeStart = Math.max(0, scopeIdx - 3);
            const scopeEnd = Math.min(gameMoves.length - 1, scopeIdx + 3);
            gameMoves = gameMoves.slice(scopeStart, scopeEnd + 1);
          }
        }

        setMoves(gameMoves);

        // Position the navigator at the target move
        let matchIdx = -1;
        if (targetReference) {
          matchIdx = findReferencedMoveIndex(gameMoves, targetReference);
        } else {
          matchIdx = gameMoves.findIndex((m) => m.fenBefore === board.fen);
          if (matchIdx < 0) {
            const fenParts = board.fen.split(" ");
            const fullmoveNumber = Number.parseInt(fenParts[fenParts.length - 1], 10);
            if (!Number.isNaN(fullmoveNumber)) {
              matchIdx = findReferencedMoveIndex(gameMoves, { moveNumber: fullmoveNumber, ply: null });
            }
          }
        }
        setNavIndex(matchIdx >= 0 ? matchIdx : 0);
        setNavLoaded(true);

        // Store the resolved FEN for compare boards (real FEN from DB, not hallucinated)
        if (matchIdx >= 0 && gameMoves[matchIdx]) {
          setResolvedFen(gameMoves[matchIdx].fenBefore);
        }

        // Derive orientation from player color
        if (analysis.playerColor) {
          setDerivedOrientation(analysis.playerColor === "b" ? "black" : "white");
        } else {
          const firstPlayerMove = gameMoves.find((m) => m.isPlayerMove);
          if (firstPlayerMove) {
            const sideToMove = firstPlayerMove.fenBefore.split(" ")[1];
            setDerivedOrientation(sideToMove === "b" ? "black" : "white");
          }
        }
      })
      .catch((err) => {
        console.error("[InlineBoard] Failed to fetch analysis:", board.gameId, err);
      });
  }, [board]);

  // Determine what to show: navigated move or original message position
  const hasPositionRef = "positionRef" in board && board.positionRef != null;
  const isNavigating = navIndex >= 0 && navLoaded;
  const currentMove = isNavigating ? moves[navIndex] : null;

  // evaluate_line navigator: a live Stockfish continuation with no
  // gameId/DB row behind it — independent of the moves/currentMove
  // state above, which is only ever populated from stored game
  // analysis. See live-eval.ts's evaluatedLineStore.
  const evalLineId = "evalLineId" in board ? board.evalLineId : null;
  const evaluatedLine = evalLineId ? evaluatedLineStore.get(evalLineId) : undefined;
  const [evalLineStepIndex, setEvalLineStepIndex] = useState(0); // 0 = first move of the line, shown immediately (no redundant "start" step showing the same position with no arrow-context change)
  const evalLineCurrentStep = evaluatedLine ? evaluatedLine.steps[evalLineStepIndex] : null;

  const displayFen = evaluatedLine
    ? // Show the position BEFORE the current step's move (same convention
      // as BestMoveNavigator): step 0 = the original evaluate_line
      // position, step N = the position after step N-1's move, i.e. right
      // before step N's move happens. This was previously using
      // evalLineCurrentStep.fenAfter, which shows the position AFTER that
      // step's move already played, making its own arrow (drawn for that
      // same step) point at a move that had already happened, one step
      // ahead of what the board displayed.
      evalLineStepIndex <= 0
      ? evaluatedLine.fen
      : evaluatedLine.steps[evalLineStepIndex - 1].fenAfter
    : currentMove
      ? currentMove.fenBefore
      : (resolvedFen ?? board.fen);

  // If we have a positionRef but haven't resolved the real FEN yet, show loading
  const isLoading = hasPositionRef && !navLoaded;
  const compareFen = resolvedFen ?? displayFen;
  useEffect(() => setMarkerFocus(null), [displayFen, compareFen]);
  const selectMarker = (fen: string, square: Square) =>
    setMarkerFocus((current) => (current?.fen === fen && current.square === square ? null : { fen, square }));

  // Keep one primary move arrow alongside the position relationships.
  let displayArrows: Arrow[];
  let navSquareStyles: CustomSquareStyles = {};

  if (evaluatedLine) {
    // Show the CURRENT step's move as the arrow — step -1 (start) shows
    // the first move about to happen, matching BestMoveNavigator's
    // "show the move that's about to occur from this position" pattern.
    const stepToShow = evalLineCurrentStep ?? evaluatedLine.steps[0] ?? null;
    displayArrows = stepToShow
      ? [chessArrow(stepToShow.from as Square, stepToShow.to as Square, "rgb(34,197,94)")]
      : [];
  } else if (currentMove) {
    const presentation = inlineMoveEvidencePresentation(currentMove);
    displayArrows = presentation.arrows;
    navSquareStyles = presentation.squareStyles;
  } else {
    // Showing original message position — use the message's own arrows
    // Keep the first message arrow primary; highlight its secondary targets.
    displayArrows =
      board.arrows.length > 0
        ? [
            chessArrow(
              board.arrows[0].from as Square,
              board.arrows[0].to as Square,
              board.arrows[0].color ?? DEFAULT_ARROW_COLOR,
            ),
          ]
        : [];
    // Show remaining arrows as highlighted squares instead
    if (board.arrows.length > 1) {
      for (const a of board.arrows.slice(1)) {
        navSquareStyles[a.to as Square] = { backgroundColor: a.color ? `${a.color}66` : "rgba(59,130,246,0.4)" };
      }
    }
  }

  // Highlights: merge message highlights + nav highlights
  const displaySquareStyles: CustomSquareStyles = {
    ...(currentMove
      ? navSquareStyles
      : {
          ...board.highlights.reduce<CustomSquareStyles>((styles, h) => {
            styles[h.square as Square] = { backgroundColor: h.color ?? DEFAULT_HIGHLIGHT_COLOR };
            return styles;
          }, {}),
          ...navSquareStyles,
        }),
  };

  // Compute eval bar percentage (0-100, where 50 = equal, 100 = white winning completely)
  // Clamp to a reasonable visual range and use a sigmoid-like curve so
  // small advantages show clearly but ±1000cp doesn't look different from ±2000cp
  // Use the current navigated move's eval, OR find the initial position's eval from loaded moves,
  // OR (if neither has this exact position) evaluate it live client-side —
  // see live-eval.ts. Falling back to 0 here used to silently show "equal"
  // for positions that were never actually evaluated as equal at all.
  const storedMove =
    currentMove ?? (moves.length > 0 ? moves.find((move) => move.fenBefore === displayFen) : undefined);
  const storedEvalCp = storedMove?.evalBeforeCp ?? null;
  const storedEvalMate = storedMove?.evalBeforeMate ?? null;
  const storedEvaluationCandidate: EvaluationScore | null =
    storedEvalCp == null && storedEvalMate == null
      ? null
      : { cp: storedEvalMate == null ? storedEvalCp : null, mate: storedEvalMate };
  const storedEvaluation = hasUsableEvaluation(storedEvaluationCandidate) ? storedEvaluationCandidate : null;
  // evalBeforeCp is from the MOVER's perspective — convert to White's perspective for the bar
  // If it's Black to move in the displayed position, negate (so bar always shows White=up, Black=down)
  const isBlackToMove = displayFen.split(" ")[1] === "b";
  const progressiveEvaluation = useProgressiveEvaluation({
    fen: displayFen,
    enabled: storedEvaluation == null,
    maxDepth: REVIEW_EVALUATION_DEPTH[settings.evaluationPreset],
  });
  const evaluation =
    normalizeEvaluation(storedEvaluation, isBlackToMove ? "b" : "w") ?? progressiveEvaluation.evaluation;

  if (isLoading) {
    return (
      <Box
        sx={{
          my: 1.5,
          mx: "auto",
          width: "min(100%, 560px)",
          aspectRatio: "1 / 1",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          border: "1px solid",
          borderColor: "divider",
          bgcolor: "background.paper",
        }}
      >
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          Loading position...
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      data-testid="nikola-inline-board"
      sx={{
        my: 1.5,
        mx: "auto",
        width: "compare" in board && board.compare ? "min(100%, 780px)" : "min(100%, 560px)",
        minWidth: 0,
      }}
    >
      <IconButton aria-label="Nikola board settings" onClick={() => setSettingsOpen(true)}>
        <SettingsIcon />
      </IconButton>
      <ReviewBoardSettingsDialog
        context="nikola"
        open={settingsOpen}
        settings={settings}
        onChange={change}
        onClose={() => setSettingsOpen(false)}
      />
      {[title, username, opponent, currentMove].some(Boolean) && (
        <Typography
          sx={{
            minHeight: 44,
            mb: 0.5,
            px: 1.25,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: ".62rem",
            fontWeight: 900,
            textTransform: "uppercase",
            letterSpacing: "0.1em",
            color: "primary.main",
            border: "1px solid",
            borderColor: "divider",
            bgcolor: "background.paper",
          }}
        >
          {username && <span style={{ color: "#888" }}>{username} </span>}
          {opponent && `vs ${opponent}`}
          {currentMove && (
            <span style={{ color: "#f0ece2" }}>{` · Move ${currentMove.moveNumber}: ${currentMove.san}`}</span>
          )}
          {!opponent && title && ` · ${title}`}
        </Typography>
      )}
      {"compare" in board && board.compare ? (
        (() => {
          // Compute eval bars for comparison: "what you played" vs "best move"
          const evalBefore = currentMove?.evalBeforeCp ?? null;
          const cpl = currentMove?.centipawnLoss ?? 0;
          const isBlackToMoveHere = (resolvedFen ?? displayFen).split(" ")[1] === "b";

          // "What you played" eval = after the bad move (lost CPL)
          const playedEvaluation =
            evalBefore == null
              ? null
              : normalizeEvaluation({ cp: evalBefore - cpl, mate: null }, isBlackToMoveHere ? "b" : "w");

          // "Best move" eval = preserves the advantage
          const bestEvaluation =
            evalBefore == null
              ? null
              : normalizeEvaluation({ cp: evalBefore, mate: null }, isBlackToMoveHere ? "b" : "w");

          return (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" },
                gap: 2,
              }}
            >
              <Box sx={{ minWidth: 0, textAlign: "center" }}>
                <Typography
                  variant="caption"
                  sx={{ color: "#ef4444", fontWeight: 700, textTransform: "uppercase", mb: 0.5, display: "block" }}
                >
                  What you played
                </Typography>
                <BoardFrame
                  evaluation={
                    settings.showEvaluation
                      ? {
                          evaluation: playedEvaluation,
                          orientation: derivedOrientation,
                          pending: progressiveEvaluation.pending && playedEvaluation == null,
                        }
                      : undefined
                  }
                >
                  <Box sx={{ width: "100%", border: "1px solid", borderColor: "divider" }}>
                    <ChesslaBoardSurface
                      settings={settings}
                      relationshipFocus={markerFocus}
                      id={`compare-played-${instanceId}`}
                      position={resolvedFen ?? displayFen}
                      onSquareClick={(square) => selectMarker(compareFen, square)}
                      orientation={derivedOrientation}
                      width="100%"
                      animationDuration={0}
                      allowDragging={false}
                      arrows={[
                        ...(settings.showBestMoveArrow
                          ? [
                              chessArrow(
                                board.compare.playedFrom as Square,
                                board.compare.playedTo as Square,
                                "rgb(239,68,68)",
                              ),
                            ]
                          : []),
                      ]}
                    />
                  </Box>
                </BoardFrame>
                <CapturedPieces fen={resolvedFen ?? displayFen} />
              </Box>
              <Box sx={{ minWidth: 0, textAlign: "center" }}>
                <Typography
                  variant="caption"
                  sx={{ color: "#22c55e", fontWeight: 700, textTransform: "uppercase", mb: 0.5, display: "block" }}
                >
                  Best move
                </Typography>
                <Box sx={{ width: "100%" }}>
                  <BestMoveNavigator
                    startingFen={resolvedFen ?? displayFen}
                    bestMoveSan={currentMove?.bestMoveSan ?? ""}
                    bestFrom={board.compare.bestFrom}
                    bestTo={board.compare.bestTo}
                    continuation={currentMove?.bestContinuation ?? []}
                    continuationEvals={currentMove?.bestContinuationEvals ?? null}
                    continuationScores={currentMove?.bestContinuationScores ?? null}
                    orientation={derivedOrientation}
                    boardWidth="100%"
                    evaluation={bestEvaluation}
                  />
                </Box>
              </Box>
            </Box>
          );
        })()
      ) : (
        <Box sx={{ width: "100%", minWidth: 0 }}>
          <BoardFrame
            evaluation={
              settings.showEvaluation
                ? {
                    evaluation,
                    orientation: derivedOrientation,
                    pending: progressiveEvaluation.pending && evaluation == null,
                  }
                : undefined
            }
          >
            <Box sx={{ width: "100%", minWidth: 0, border: "1px solid", borderColor: "divider" }}>
              <ChesslaBoardSurface
                settings={settings}
                relationshipFocus={markerFocus}
                id={`inline-${instanceId}`}
                position={displayFen}
                onSquareClick={(square) => selectMarker(displayFen, square)}
                orientation={derivedOrientation}
                width="100%"
                animationDuration={0}
                allowDragging={false}
                arrows={settings.showBestMoveArrow ? displayArrows : []}
                squareStyles={settings.showPositionMarkers ? displaySquareStyles : {}}
              />
            </Box>
          </BoardFrame>
          <CapturedPieces fen={displayFen} />
        </Box>
      )}
      {evaluatedLine && evaluatedLine.steps.length > 0 && (
        <Box sx={{ mt: 1, p: 1, border: "2px solid rgba(255,255,255,0.1)", bgcolor: "background.paper" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, justifyContent: "center" }}>
            <IconButton
              size="small"
              onClick={() => setEvalLineStepIndex(0)}
              disabled={evalLineStepIndex <= 0}
              sx={{ color: "#22c55e", "&:disabled": { color: "#333" } }}
            >
              <SkipPreviousIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => setEvalLineStepIndex((i) => Math.max(0, i - 1))}
              disabled={evalLineStepIndex <= 0}
              sx={{ color: "#22c55e", "&:disabled": { color: "#333" } }}
            >
              <NavigateBeforeIcon fontSize="small" />
            </IconButton>
            <Typography
              variant="caption"
              sx={{ minWidth: 100, textAlign: "center", fontWeight: 700, color: "#22c55e" }}
            >
              {evalLineCurrentStep
                ? `${evalLineStepIndex + 1}/${evaluatedLine.steps.length}: ${evalLineCurrentStep.san}`
                : ""}
            </Typography>
            <IconButton
              size="small"
              onClick={() => setEvalLineStepIndex((i) => Math.min(evaluatedLine.steps.length - 1, i + 1))}
              disabled={evalLineStepIndex >= evaluatedLine.steps.length - 1}
              sx={{ color: "#22c55e", "&:disabled": { color: "#333" } }}
            >
              <NavigateNextIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => setEvalLineStepIndex(evaluatedLine.steps.length - 1)}
              disabled={evalLineStepIndex >= evaluatedLine.steps.length - 1}
              sx={{ color: "#22c55e", "&:disabled": { color: "#333" } }}
            >
              <SkipNextIcon fontSize="small" />
            </IconButton>
          </Box>
          <Typography
            variant="caption"
            sx={{ display: "block", textAlign: "center", mt: 0.5, color: "text.secondary" }}
          >
            Verified analysis line, depth {evaluatedLine.depth}
          </Typography>
        </Box>
      )}
      {board.gameId && navLoaded && currentMove && !("compare" in board && board.compare) ? (
        <InlineMoveRail
          move={currentMove}
          ply={recordedPly(currentMove)}
          totalPlies={totalPlies}
          canGoBack={navIndex > 0}
          canGoForward={navIndex < moves.length - 1}
          onFirst={() => setNavIndex(0)}
          onPrevious={() => setNavIndex((index) => Math.max(0, index - 1))}
          onNext={() => setNavIndex((index) => Math.min(moves.length - 1, index + 1))}
          onLast={() => setNavIndex(moves.length - 1)}
        />
      ) : null}
    </Box>
  );
};
